import {
  arrayUnion,
  collection,
  deleteField,
  doc,
  limit,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type Query,
  type Unsubscribe,
  type WriteBatch,
} from '@firebase/firestore';
import { httpsCallable } from '@firebase/functions';
import { db, functions } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import {
  RepEditChanges,
  Vendor,
  VendorEdit,
  VendorEditChanges,
  VendorRating,
  VendorRep,
  VendorStatus,
} from '../types';
import { mapDocs } from '@utils/firestore';
import {
  VendorSchema,
  VendorRepSchema,
  VendorEditSchema,
  VendorRatingSchema,
} from '../schemas/firestoreSchemas';
import { createPrefixedLogger } from '../utils/logger';
import { vendorNameKey, websiteDomain } from '../utils/vendors';
import type { VendorUploadPlan, ParsedRep } from '../utils/vendorImport';

const log = createPrefixedLogger('api:vendors');

// The shared vendor directory (Nov 2026). See the vendors/vendorReps block in
// firestore.rules: a non-moderator's list query must carry
// status == 'approved' or createdBy == uid, or the whole query is denied.

// Firestore allows 500 operations per batch; stay well under it.
export const VENDOR_BATCH_SIZE = 400;
const DIRECTORY_LIMIT = 1000;

export interface VendorDirectory {
  vendors: Vendor[];
  reps: VendorRep[];
}

function vendorsCollection() {
  return collection(db, COLLECTIONS.VENDORS);
}
function vendorDoc(id: string) {
  return doc(db, COLLECTIONS.VENDORS, id);
}
function repsCollection() {
  return collection(db, COLLECTIONS.VENDOR_REPS);
}
function repDoc(id: string) {
  return doc(db, COLLECTIONS.VENDOR_REPS, id);
}
function editsCollection() {
  return collection(db, COLLECTIONS.VENDOR_EDITS);
}
function editDoc(id: string) {
  return doc(db, COLLECTIONS.VENDOR_EDITS, id);
}
function ratingsCollection() {
  return collection(db, COLLECTIONS.VENDOR_RATINGS);
}
function ratingDoc(id: string) {
  return doc(db, COLLECTIONS.VENDOR_RATINGS, id);
}
function bidDoc(planId: string, bidId: string) {
  return doc(db, COLLECTIONS.DEFENSE_PLANS, planId, COLLECTIONS.DEFENSE_PLAN_BIDS, bidId);
}

/** One rating per account per vendor; rules check the id. */
export function vendorRatingId(vendorId: string, uid: string): string {
  return `${vendorId}__${uid}`;
}

// Firestore rejects undefined values; optional fields are absent instead.
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

function byName(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

// Two listeners (vendors, reps) folded into one feed. Nothing is emitted
// until both have delivered, so a caller never sees reps with no vendors.
function subscribePair(
  vendorQuery: Query,
  repQuery: Query,
  label: string,
  onNext: (dir: VendorDirectory) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  let vendors: Vendor[] | null = null;
  let reps: VendorRep[] | null = null;
  const emit = () => {
    if (vendors && reps) onNext({ vendors, reps });
  };
  const fail = (which: string) => (error: Error) => {
    log.error(`${label} ${which} failed:`, error);
    onError?.(error);
  };
  const unsubVendors = onSnapshotWithRetry(
    vendorQuery,
    (snap) => {
      vendors = mapDocs(snap, VendorSchema);
      vendors.sort((a, b) => byName(a.organization, b.organization));
      emit();
    },
    fail('vendors')
  );
  const unsubReps = onSnapshotWithRetry(
    repQuery,
    (snap) => {
      reps = mapDocs(snap, VendorRepSchema);
      reps.sort((a, b) => byName(a.name, b.name));
      emit();
    },
    fail('reps')
  );
  return () => {
    unsubVendors();
    unsubReps();
  };
}

export interface RepInput {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

export interface SuggestVendorInput {
  organization: string;
  website?: string;
  categories: string[];
  areaIds: string[];
  notes?: string;
  rep?: RepInput;
}

export interface SuggestRepInput extends RepInput {
  areaIds: string[];
}

/** A moderator edit. '' clears an optional string; null clears cfscListed. */
export interface VendorPatch {
  organization?: string;
  website?: string;
  categories?: string[];
  areaIds?: string[];
  serviceAreaText?: string;
  licenses?: string;
  cfscListed?: boolean | null;
  notes?: string;
}

export interface RepPatch {
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
  areaIds?: string[];
}

export interface VendorUploadResult {
  vendorsCreated: number;
  vendorsUpdated: number;
  repsCreated: number;
}

function repData(
  vendorId: string,
  rep: RepInput & { areaIds: string[] },
  meta: { status: VendorStatus; source: 'user' | 'upload'; createdBy: string }
) {
  return stripUndefined({
    vendorId,
    name: rep.name.trim(),
    role: rep.role?.trim() || undefined,
    email: rep.email?.trim() || undefined,
    phone: rep.phone?.trim() || undefined,
    areaIds: rep.areaIds,
    status: meta.status,
    source: meta.source,
    createdBy: meta.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(meta.source === 'upload'
      ? { reviewedBy: meta.createdBy, reviewedAt: serverTimestamp() }
      : {}),
  });
}

function uploadRepData(vendorId: string, rep: ParsedRep, moderatorUid: string) {
  return repData(vendorId, rep, { status: 'approved', source: 'upload', createdBy: moderatorUid });
}

/** What findVendorCandidates returns (functions/src/vendors/findVendorCandidates.ts). */
export interface VendorSearchResult {
  runId: string;
  written: number;
  dropped: number;
  alreadyListed: number;
}

// A search runs several model calls with web searches between them; the
// callable allows five minutes, and the client's default (70 s) would give up
// first.
const VENDOR_SEARCH_TIMEOUT_MS = 300_000;

function vendorPatchData(patch: VendorPatch): Record<string, unknown> {
  const data: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (patch.organization !== undefined) {
    const organization = patch.organization.trim();
    data.organization = organization;
    data.nameKey = vendorNameKey(organization);
  }
  if (patch.website !== undefined) {
    const website = patch.website.trim();
    data.website = website || deleteField();
    data.domain = websiteDomain(website) ?? deleteField();
  }
  if (patch.categories !== undefined) data.categories = patch.categories;
  if (patch.areaIds !== undefined) data.areaIds = patch.areaIds;
  for (const key of ['serviceAreaText', 'licenses', 'notes'] as const) {
    const value = patch[key];
    if (value !== undefined) data[key] = value.trim() || deleteField();
  }
  if (patch.cfscListed !== undefined) data.cfscListed = patch.cfscListed ?? deleteField();
  return data;
}

function repPatchData(patch: RepPatch): Record<string, unknown> {
  const data: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (patch.name !== undefined) data.name = patch.name.trim();
  for (const key of ['role', 'email', 'phone'] as const) {
    const value = patch[key];
    if (value !== undefined) data[key] = value.trim() || deleteField();
  }
  if (patch.areaIds !== undefined) data.areaIds = patch.areaIds;
  return data;
}

export interface SuggestEditInput {
  vendorId: string;
  /** Set when the fix is to a contact rather than the company. */
  repId?: string;
  changes: VendorEditChanges | RepEditChanges;
  note?: string;
}

export interface RateVendorInput {
  vendorId: string;
  planId: string;
  bidId: string;
  stars: number;
  note?: string;
  /** True when the bid isn't linked to this vendor yet: the link and the
   *  rating go in one batch, because the rating rule reads the bid after. */
  linkBid: boolean;
}

export const vendorsApi = {
  newVendorId(): string {
    return doc(vendorsCollection()).id;
  },
  newRepId(): string {
    return doc(repsCollection()).id;
  },

  /** Every approved vendor and rep: what any signed-in account may read. */
  subscribeApproved(
    onNext: (dir: VendorDirectory) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return subscribePair(
      query(vendorsCollection(), where('status', '==', 'approved'), limit(DIRECTORY_LIMIT)),
      query(repsCollection(), where('status', '==', 'approved'), limit(DIRECTORY_LIMIT)),
      'subscribeApproved',
      onNext,
      onError
    );
  },

  /** The account's own suggestions, any status. */
  subscribeMine(
    uid: string,
    onNext: (dir: VendorDirectory) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return subscribePair(
      query(vendorsCollection(), where('createdBy', '==', uid), limit(DIRECTORY_LIMIT)),
      query(repsCollection(), where('createdBy', '==', uid), limit(DIRECTORY_LIMIT)),
      'subscribeMine',
      onNext,
      onError
    );
  },

  /** Everything, any status. Moderator only: the rules deny it otherwise. */
  subscribeAllForModerator(
    onNext: (dir: VendorDirectory) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return subscribePair(
      query(vendorsCollection()),
      query(repsCollection()),
      'subscribeAllForModerator',
      onNext,
      onError
    );
  },

  // One batch, so a new company and its first rep arrive together (the rep
  // rule reads the vendor with getAfter).
  async suggestVendor(
    uid: string,
    input: SuggestVendorInput
  ): Promise<{ vendorId: string; repId?: string }> {
    const vendorId = vendorsApi.newVendorId();
    const organization = input.organization.trim();
    const website = input.website?.trim() || undefined;
    const batch = writeBatch(db);
    batch.set(
      vendorDoc(vendorId),
      stripUndefined({
        organization,
        nameKey: vendorNameKey(organization),
        website,
        domain: websiteDomain(website),
        categories: input.categories,
        areaIds: input.areaIds,
        notes: input.notes?.trim() || undefined,
        status: 'suggested',
        source: 'user',
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    );
    let repId: string | undefined;
    if (input.rep) {
      repId = vendorsApi.newRepId();
      batch.set(
        repDoc(repId),
        repData(
          vendorId,
          { ...input.rep, areaIds: input.areaIds },
          { status: 'suggested', source: 'user', createdBy: uid }
        )
      );
    }
    await batch.commit();
    return repId ? { vendorId, repId } : { vendorId };
  },

  async suggestRep(uid: string, vendorId: string, rep: SuggestRepInput): Promise<string> {
    const repId = vendorsApi.newRepId();
    const batch = writeBatch(db);
    batch.set(
      repDoc(repId),
      repData(vendorId, rep, { status: 'suggested', source: 'user', createdBy: uid })
    );
    await batch.commit();
    return repId;
  },

  // The vendor and the reps the moderator was looking at move together.
  /**
   * Moderator only: searches the web for companies doing one kind of work
   * near the area. What it finds arrives as 'suggested' vendors with source
   * 'ai', through the moderator's directory subscription.
   */
  async findCandidates(areaId: string, category: string): Promise<VendorSearchResult> {
    const callable = httpsCallable<{ areaId: string; category: string }, VendorSearchResult>(
      functions,
      'findVendorCandidates',
      { timeout: VENDOR_SEARCH_TIMEOUT_MS }
    );
    const result = await callable({ areaId, category });
    return result.data;
  },

  async reviewVendor(
    moderatorUid: string,
    vendorId: string,
    status: 'approved' | 'rejected',
    repIds: string[]
  ): Promise<void> {
    const batch = writeBatch(db);
    const review = {
      status,
      reviewedBy: moderatorUid,
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    batch.update(vendorDoc(vendorId), review);
    for (const repId of repIds) batch.update(repDoc(repId), review);
    await batch.commit();
  },

  async reviewRep(
    moderatorUid: string,
    repId: string,
    status: 'approved' | 'rejected'
  ): Promise<void> {
    const batch = writeBatch(db);
    batch.update(repDoc(repId), {
      status,
      reviewedBy: moderatorUid,
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
  },

  // Moderator edits. '' removes an optional string. categories and areaIds
  // are required fields, so they are written as given.
  async updateVendor(vendorId: string, patch: VendorPatch): Promise<void> {
    const batch = writeBatch(db);
    batch.update(vendorDoc(vendorId), vendorPatchData(patch));
    await batch.commit();
  },

  async updateRep(repId: string, patch: RepPatch): Promise<void> {
    const batch = writeBatch(db);
    batch.update(repDoc(repId), repPatchData(patch));
    await batch.commit();
  },

  async deleteVendor(vendorId: string, repIds: string[]): Promise<void> {
    const batch = writeBatch(db);
    batch.delete(vendorDoc(vendorId));
    for (const repId of repIds) batch.delete(repDoc(repId));
    await batch.commit();
  },

  // Writes the output of planVendorUpload. New vendors and reps are created
  // approved; matches only gain categories, areas, filled-in blanks and new
  // reps. Nothing is ever set over an existing doc. A failed chunk throws and
  // leaves earlier chunks written, which is safe to re-run: the planner
  // matches the created vendors by nameKey the second time.
  async applyVendorUpload(
    moderatorUid: string,
    plan: VendorUploadPlan
  ): Promise<VendorUploadResult> {
    const ops: ((batch: WriteBatch) => void)[] = [];
    let repsCreated = 0;

    for (const v of plan.creates) {
      const vendorId = vendorsApi.newVendorId();
      ops.push((batch) =>
        batch.set(
          vendorDoc(vendorId),
          stripUndefined({
            organization: v.organization,
            nameKey: v.nameKey,
            website: v.website,
            domain: v.domain,
            categories: v.categories,
            areaIds: v.areaIds,
            serviceAreaText: v.serviceAreaText,
            licenses: v.licenses,
            cfscListed: v.cfscListed,
            notes: v.notes,
            status: 'approved',
            source: 'upload',
            createdBy: moderatorUid,
            reviewedBy: moderatorUid,
            reviewedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
        )
      );
      for (const rep of v.reps) {
        const repId = vendorsApi.newRepId();
        repsCreated += 1;
        ops.push((batch) => batch.set(repDoc(repId), uploadRepData(vendorId, rep, moderatorUid)));
      }
    }

    for (const m of plan.merges) {
      const data: Record<string, unknown> = stripUndefined({
        ...m.fill,
        updatedAt: serverTimestamp(),
      });
      if (m.addCategories.length > 0) data.categories = arrayUnion(...m.addCategories);
      if (m.addAreaIds.length > 0) data.areaIds = arrayUnion(...m.addAreaIds);
      ops.push((batch) => batch.update(vendorDoc(m.vendorId), data));
      for (const rep of m.newReps) {
        const repId = vendorsApi.newRepId();
        repsCreated += 1;
        ops.push((batch) => batch.set(repDoc(repId), uploadRepData(m.vendorId, rep, moderatorUid)));
      }
    }

    for (let i = 0; i < ops.length; i += VENDOR_BATCH_SIZE) {
      const batch = writeBatch(db);
      for (const op of ops.slice(i, i + VENDOR_BATCH_SIZE)) op(batch);
      await batch.commit();
    }

    return {
      vendorsCreated: plan.creates.length,
      vendorsUpdated: plan.merges.length,
      repsCreated,
    };
  },

  // ----- suggested edits (Nov 2026) -----

  /** Edits waiting for review. Moderator only. */
  subscribeEditsForModerator(
    onNext: (edits: VendorEdit[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      query(editsCollection(), where('status', '==', 'suggested'), limit(DIRECTORY_LIMIT)),
      (snap) => onNext(mapDocs(snap, VendorEditSchema)),
      (error) => {
        log.error('subscribeEditsForModerator failed:', error);
        onError?.(error);
      }
    );
  },

  /** The account's own edits, any status. */
  subscribeMyEdits(
    uid: string,
    onNext: (edits: VendorEdit[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      query(editsCollection(), where('createdBy', '==', uid), limit(DIRECTORY_LIMIT)),
      (snap) => onNext(mapDocs(snap, VendorEditSchema)),
      (error) => {
        log.error('subscribeMyEdits failed:', error);
        onError?.(error);
      }
    );
  },

  async suggestEdit(uid: string, input: SuggestEditInput): Promise<string> {
    const ref = doc(editsCollection());
    const batch = writeBatch(db);
    batch.set(
      ref,
      stripUndefined({
        vendorId: input.vendorId,
        repId: input.repId,
        changes: input.changes,
        note: input.note?.trim() || undefined,
        status: 'suggested',
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    );
    await batch.commit();
    return ref.id;
  },

  /** Approving applies the change to the vendor or rep in the same batch,
   *  through the same field handling as a moderator's own edit. */
  async reviewEdit(
    moderatorUid: string,
    edit: VendorEdit,
    status: 'approved' | 'rejected'
  ): Promise<void> {
    const batch = writeBatch(db);
    if (status === 'approved') {
      if (edit.repId) batch.update(repDoc(edit.repId), repPatchData(edit.changes as RepPatch));
      else batch.update(vendorDoc(edit.vendorId), vendorPatchData(edit.changes as VendorPatch));
    }
    batch.update(editDoc(edit.id), {
      status,
      reviewedBy: moderatorUid,
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
  },

  /** The author takes back a pending edit. */
  async withdrawEdit(editId: string): Promise<void> {
    const batch = writeBatch(db);
    batch.delete(editDoc(editId));
    await batch.commit();
  },

  // ----- ratings (Nov 2026) -----

  /** Ratings a moderator hasn't hidden: what any directory reader may list. */
  subscribeVisibleRatings(
    onNext: (ratings: VendorRating[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      query(ratingsCollection(), where('hidden', '==', false), limit(DIRECTORY_LIMIT)),
      (snap) => onNext(mapDocs(snap, VendorRatingSchema)),
      (error) => {
        log.error('subscribeVisibleRatings failed:', error);
        onError?.(error);
      }
    );
  },

  /** The account's own ratings, hidden or not. */
  subscribeMyRatings(
    uid: string,
    onNext: (ratings: VendorRating[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      query(ratingsCollection(), where('createdBy', '==', uid), limit(DIRECTORY_LIMIT)),
      (snap) => onNext(mapDocs(snap, VendorRatingSchema)),
      (error) => {
        log.error('subscribeMyRatings failed:', error);
        onError?.(error);
      }
    );
  },

  /** Every rating, hidden ones too. Moderator only. */
  subscribeAllRatingsForModerator(
    onNext: (ratings: VendorRating[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      query(ratingsCollection(), limit(DIRECTORY_LIMIT)),
      (snap) => onNext(mapDocs(snap, VendorRatingSchema)),
      (error) => {
        log.error('subscribeAllRatingsForModerator failed:', error);
        onError?.(error);
      }
    );
  },

  async rateVendor(uid: string, input: RateVendorInput): Promise<string> {
    const id = vendorRatingId(input.vendorId, uid);
    const batch = writeBatch(db);
    if (input.linkBid) {
      batch.update(bidDoc(input.planId, input.bidId), {
        vendorId: input.vendorId,
        updatedAt: serverTimestamp(),
      });
    }
    batch.set(
      ratingDoc(id),
      stripUndefined({
        vendorId: input.vendorId,
        createdBy: uid,
        stars: input.stars,
        note: input.note?.trim() || undefined,
        planId: input.planId,
        bidId: input.bidId,
        hidden: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    );
    await batch.commit();
    return id;
  },

  /** The author changes their stars or note. '' removes the note. */
  async updateRating(ratingId: string, stars: number, note: string): Promise<void> {
    const batch = writeBatch(db);
    batch.update(ratingDoc(ratingId), {
      stars,
      note: note.trim() || deleteField(),
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
  },

  async setRatingHidden(moderatorUid: string, ratingId: string, hidden: boolean): Promise<void> {
    const batch = writeBatch(db);
    batch.update(ratingDoc(ratingId), {
      hidden,
      hiddenBy: hidden ? moderatorUid : deleteField(),
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
  },

  async deleteRating(ratingId: string): Promise<void> {
    const batch = writeBatch(db);
    batch.delete(ratingDoc(ratingId));
    await batch.commit();
  },
};
